import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import type { Project } from '@veodiagram/shared';
import { api, type ServerCapabilities } from '../services/api';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import { StructuralSummary } from '../components/dashboard/StructuralSummary';
import { OperationalSummary } from '../components/dashboard/OperationalSummary';
import { SecuritySummary } from '../components/dashboard/SecuritySummary';
import { DashboardQuickNav } from '../components/dashboard/DashboardQuickNav';
import { GithubRepoPicker } from '../components/github/GithubRepoPicker';
import { GithubConnectBanner } from '../components/github/GithubConnectBanner';
import { ConnectedReposPanel } from '../components/github/ConnectedReposPanel';

type SourceType = 'github' | 'local';
type SourceRole = 'frontend' | 'backend' | 'shared' | 'unknown';

// #1336 follow-up — per-source draft state for the multi-source
// project-creation form. The first row becomes the project's primary
// (its repoUrl/localPath/sourceType live on Project for legacy
// compat); subsequent rows are POSTed via /sources right after the
// project is created.
interface SourceDraft {
  id: string;
  label: string;
  role: SourceRole;
  sourceType: SourceType;
  repoUrl: string;
  localPath: string;
  baseUrl: string;
}

function emptySource(defaultType: SourceType): SourceDraft {
  return {
    id: Math.random().toString(36).slice(2, 10),
    label: '',
    role: 'unknown',
    sourceType: defaultType,
    repoUrl: '',
    localPath: '',
    baseUrl: '',
  };
}

export function DashboardPage() {
  const { isAuthenticated } = useAuthStore();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [sources, setSources] = useState<SourceDraft[]>(() => [emptySource('github')]);
  const [capabilities, setCapabilities] = useState<ServerCapabilities | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [deletePreview, setDeletePreview] = useState<{
    diagramCount: number;
    analysisJobCount: number;
  } | null>(null);

  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    // Fetch server capabilities — issue #855: handle failure gracefully.
    api.getCapabilities()
      .then((caps) => {
        setCapabilities(caps);
        setServerError(null);
      })
      .catch((err) => {
        console.error('Failed to load capabilities:', err);
        setServerError(err?.message ?? 'Cannot connect to server');
      });
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadProjects();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Set default source type based on capabilities. Affects the first
  // empty source row; user can still override per-row.
  useEffect(() => {
    if (capabilities) {
      if (capabilities.localAnalysis && capabilities.authMode === 'local') {
        setSources(prev =>
          prev.map((s, i) => (i === 0 && !s.repoUrl && !s.localPath ? { ...s, sourceType: 'local' } : s)),
        );
      }
    }
  }, [capabilities]);

  const loadProjects = async () => {
    try {
      const data = await api.getProjects();
      setProjects(data);
      setServerError(null);
    } catch (error: any) {
      console.error('Failed to load projects:', error);
      if (error?.message?.includes('Cannot connect')) {
        setServerError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);

    // Validate: at least one source row, each row has its path/url.
    if (sources.length === 0) {
      setCreateError('Add at least one source.');
      return;
    }
    for (const s of sources) {
      if (s.sourceType === 'local' && !s.localPath.trim()) {
        setCreateError(`Source "${s.label || 'untitled'}" is missing a local path.`);
        return;
      }
      if (s.sourceType === 'github' && !s.repoUrl.trim()) {
        setCreateError(`Source "${s.label || 'untitled'}" is missing a repository URL.`);
        return;
      }
    }

    try {
      // First source seeds the project's primary repoUrl/localPath/sourceType
      // for legacy single-source compatibility.
      const primary = sources[0];
      const projectData =
        primary.sourceType === 'local'
          ? {
              name: newProjectName,
              localPath: primary.localPath.trim(),
              sourceType: 'local' as const,
            }
          : {
              name: newProjectName,
              repoUrl: primary.repoUrl.trim() || undefined,
              sourceType: 'github' as const,
            };

      const project = await api.createProject(projectData);

      // Additional sources are POSTed via /sources after creation.
      // Errors here don't block project creation — the project is
      // already saved; we surface the message but keep it in the list.
      const extra = sources.slice(1);
      const failures: string[] = [];
      for (let i = 0; i < extra.length; i++) {
        const s = extra[i];
        try {
          await api.addSource(project.id, {
            label: s.label.trim() || `source-${i + 2}`,
            role: s.role,
            sourceType: s.sourceType,
            ...(s.sourceType === 'local'
              ? { localPath: s.localPath.trim() }
              : { repoUrl: s.repoUrl.trim() }),
            ...(s.baseUrl.trim() ? { baseUrl: s.baseUrl.trim() } : {}),
          });
        } catch (err) {
          failures.push(`${s.label || `source-${i + 2}`}: ${(err as Error).message}`);
        }
      }

      // Also push the FIRST source through /sources when the user gave
      // it source-specific metadata (label/role/baseUrl) — the
      // project-level fields don't carry those.
      const hasFirstSourceMetadata =
        primary.label.trim() !== '' ||
        primary.role !== 'unknown' ||
        primary.baseUrl.trim() !== '';
      if (hasFirstSourceMetadata) {
        try {
          await api.addSource(project.id, {
            label: primary.label.trim() || 'primary',
            role: primary.role,
            sourceType: primary.sourceType,
            ...(primary.sourceType === 'local'
              ? { localPath: primary.localPath.trim() }
              : { repoUrl: primary.repoUrl.trim() }),
            ...(primary.baseUrl.trim() ? { baseUrl: primary.baseUrl.trim() } : {}),
          });
        } catch (err) {
          failures.push(`primary: ${(err as Error).message}`);
        }
      }

      if (failures.length > 0) {
        setCreateError(
          `Project created, but some sources failed to attach:\n${failures.join('\n')}`,
        );
      }

      setProjects([project, ...projects]);
      setShowNewProject(false);
      resetForm();
    } catch (error) {
      console.error('Failed to create project:', error);
      setCreateError(error instanceof Error ? error.message : 'Failed to create project');
    }
  };

  const resetForm = () => {
    setNewProjectName('');
    setSources([emptySource(capabilities?.localAnalysis && capabilities?.authMode === 'local' ? 'local' : 'github')]);
    setCreateError(null);
  };

  const updateSource = (id: string, patch: Partial<SourceDraft>) => {
    setSources(prev => prev.map(s => (s.id === id ? { ...s, ...patch } : s)));
  };
  const addSourceRow = () => {
    setSources(prev => [
      ...prev,
      emptySource(capabilities?.localAnalysis && capabilities?.authMode === 'local' ? 'local' : 'github'),
    ]);
  };
  const removeSourceRow = (id: string) => {
    setSources(prev => (prev.length <= 1 ? prev : prev.filter(s => s.id !== id)));
  };

  const handleCancelCreate = () => {
    setShowNewProject(false);
    resetForm();
  };

  const handleDeleteClick = async (e: React.MouseEvent, project: Project) => {
    e.preventDefault();
    e.stopPropagation();
    setProjectToDelete(project);
    try {
      const preview = await api.getProjectDeletePreview(project.id);
      setDeletePreview({
        diagramCount: preview.diagramCount,
        analysisJobCount: preview.analysisJobCount,
      });
    } catch (error) {
      console.error('Failed to get delete preview:', error);
      setDeletePreview(null);
    }
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete) return;
    await api.deleteProject(projectToDelete.id);
    setProjects(projects.filter(p => p.id !== projectToDelete.id));
    setProjectToDelete(null);
    setDeletePreview(null);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setProjectToDelete(null);
    setDeletePreview(null);
  };

  const showLocalOption = capabilities?.localAnalysis;
  const showGitHubOption = capabilities?.authMode !== 'local';

  if (!isAuthenticated) {
    return (
      <div className="container mx-auto px-4 py-8">
        {serverError && (
          <div className="mb-4 p-4 border border-red-200 dark:border-red-800 rounded bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
            {serverError}
          </div>
        )}
        <p className="text-center text-muted-foreground">
          Please sign in to view your dashboard.
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/*
        Issue #819 review follow-up: make the page primary-CTA-first.
        The three dashboard summaries include deliberately-synthesised
        operational and security data (no real adapters configured by
        default), and users who just want to reach their projects
        should not have to scroll past them. Quick-nav lives at the
        top and the project list is the first section below it;
        dashboard summaries follow, clearly labelled live vs preview.
      */}
      <DashboardQuickNav projectCount={projects.length} />

      <GithubConnectBanner />

      {/* Repo ↔ project map; renders only when installations exist (the
          banner above covers the zero-state, so exactly one shows). */}
      <ConnectedReposPanel
        projects={projects}
        onUseRepo={(url) => {
          setShowNewProject(true);
          setSources((prev) => {
            const next = [...prev];
            next[0] = { ...next[0], sourceType: 'github', repoUrl: url };
            return next;
          });
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />

      <section
        id="your-projects"
        tabIndex={-1}
        className="scroll-mt-20"
      >
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">Your Projects</h1>
        <div className="flex items-center gap-3">
          <Link
            to="/trash"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            title="View trash"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Trash
          </Link>
          <button
            onClick={() => setShowNewProject(true)}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            New Project
          </button>
        </div>
      </div>

      {showNewProject && (
        <div className="mb-8 p-6 border border-border rounded-lg">
          <h2 className="text-lg font-semibold mb-4">Create New Project</h2>

          {createError && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">
              {createError}
            </div>
          )}

          <form onSubmit={handleCreateProject} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium mb-1">
                Project Name
              </label>
              <input
                id="name"
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
                placeholder="My Awesome Project"
                required
              />
            </div>

            {/* Sources — projects can attach multiple repos/folders.
                The first source becomes the project's primary; the rest
                are added via /sources right after the project is
                created. The cross-repo analyzer stitches calls between
                them via each source's optional baseUrl. */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium">
                  Sources <span className="text-muted-foreground">({sources.length})</span>
                </label>
                <button
                  type="button"
                  onClick={addSourceRow}
                  className="text-xs px-2 py-0.5 rounded border border-border hover:bg-muted text-foreground/80 hover:text-foreground transition-colors"
                >
                  + Add source
                </button>
              </div>
              {sources.map((s, idx) => (
                <div
                  key={s.id}
                  className="rounded-md border border-border/70 bg-muted/20 p-3 space-y-2"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {idx === 0 ? 'Primary source' : `Source ${idx + 1}`}
                    </span>
                    {sources.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSourceRow(s.id)}
                        className="ml-auto text-rose-500 hover:text-rose-700 text-sm"
                        aria-label="Remove source"
                      >
                        ×
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] text-muted-foreground mb-0.5">
                        Label {idx === 0 && <span className="opacity-60">(optional)</span>}
                      </label>
                      <input
                        type="text"
                        value={s.label}
                        onChange={e => updateSource(s.id, { label: e.target.value })}
                        placeholder={idx === 0 ? 'primary' : 'api-server'}
                        className="w-full px-2 py-1 border border-input rounded text-sm bg-background"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-muted-foreground mb-0.5">Role</label>
                      <select
                        value={s.role}
                        onChange={e => updateSource(s.id, { role: e.target.value as SourceRole })}
                        className="w-full px-2 py-1 border border-input rounded text-sm bg-background"
                      >
                        <option value="unknown">unknown</option>
                        <option value="frontend">frontend</option>
                        <option value="backend">backend</option>
                        <option value="shared">shared</option>
                      </select>
                    </div>
                  </div>

                  {showLocalOption && showGitHubOption && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => updateSource(s.id, { sourceType: 'github' })}
                        className={`flex-1 px-2 py-1 text-xs rounded border transition-colors ${
                          s.sourceType === 'github'
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background border-border hover:bg-muted'
                        }`}
                      >
                        GitHub URL
                      </button>
                      <button
                        type="button"
                        onClick={() => updateSource(s.id, { sourceType: 'local' })}
                        className={`flex-1 px-2 py-1 text-xs rounded border transition-colors ${
                          s.sourceType === 'local'
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background border-border hover:bg-muted'
                        }`}
                      >
                        Local folder
                      </button>
                    </div>
                  )}

                  {s.sourceType === 'github' && showGitHubOption && (
                    <div>
                      <GithubRepoPicker onPick={(url) => updateSource(s.id, { repoUrl: url })} />
                      <input
                        type="url"
                        value={s.repoUrl}
                        onChange={e => updateSource(s.id, { repoUrl: e.target.value })}
                        placeholder="https://github.com/owner/repo"
                        className="w-full px-2 py-1 border border-input rounded text-sm bg-background"
                      />
                    </div>
                  )}
                  {s.sourceType === 'local' && showLocalOption && (
                    <div>
                      <input
                        type="text"
                        value={s.localPath}
                        onChange={e => updateSource(s.id, { localPath: e.target.value })}
                        placeholder={capabilities?.localAnalysisBasePath ? `${capabilities.localAnalysisBasePath}/your-project` : '/path/to/your/project'}
                        className="w-full px-2 py-1 border border-input rounded text-sm font-mono bg-background"
                      />
                      {capabilities?.localAnalysisBasePath && (
                        <p className="mt-0.5 text-[11px] text-muted-foreground">
                          The app reads folders under <code>{capabilities.localAnalysisBasePath}</code> — on
                          containerized installs that&apos;s your repos folder as the app sees it.
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-[11px] text-muted-foreground mb-0.5">
                      Base URL <span className="opacity-60">(optional, for cross-repo stitching)</span>
                    </label>
                    <input
                      type="text"
                      value={s.baseUrl}
                      onChange={e => updateSource(s.id, { baseUrl: e.target.value })}
                      placeholder="https://api.example.com"
                      className="w-full px-2 py-1 border border-input rounded text-sm font-mono bg-background"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90"
              >
                Create Project
              </button>
              <button
                type="button"
                onClick={handleCancelCreate}
                className="px-4 py-2 border border-border rounded-md text-sm font-medium hover:bg-muted"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading projects...</p>
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-lg">
          <p className="text-muted-foreground mb-4">No projects yet</p>
          <button
            onClick={() => setShowNewProject(true)}
            className="text-primary hover:underline"
          >
            Create your first project
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div
              key={project.id}
              className="relative group block p-6 border border-border rounded-lg hover:border-primary transition-colors"
            >
              <Link
                to={`/projects/${project.id}`}
                className="block"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold">{project.name}</h3>
                  {project.sourceType === 'local' ? (
                    <span className="text-xs bg-muted px-2 py-1 rounded">Local</span>
                  ) : (
                    <span className="text-xs bg-muted px-2 py-1 rounded">GitHub</span>
                  )}
                </div>
                {project.description && (
                  <p className="text-sm text-muted-foreground mb-4">
                    {project.description}
                  </p>
                )}
                {project.repoUrl && (
                  <p className="text-xs text-muted-foreground truncate">
                    {project.repoUrl}
                  </p>
                )}
                {project.localPath && (
                  <p className="text-xs text-muted-foreground truncate">
                    {project.localPath}
                  </p>
                )}
              </Link>
              {/* Delete button - shown on hover */}
              <button
                onClick={(e) => handleDeleteClick(e, project)}
                className="absolute top-2 right-2 p-1.5 rounded-md opacity-0 group-hover:opacity-100 hover:bg-red-100 dark:hover:bg-red-900/30 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-all"
                title="Delete project"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      </section>

      {/*
        Dashboard summaries — structural is live, operational and
        security default to synthesised mocks until the user connects
        real observability / security adapters. Each section is
        anchored by an id so the DashboardQuickNav above can scroll
        the user into it on demand.
      */}
      <div className="mt-10 pt-6 border-t border-border">
        {/* Phase 1: Structural Summary */}
        <StructuralSummary />
        {/* Phase 2: Operational Summary */}
        <OperationalSummary />
        {/* Phase 3: Security Summary */}
        <SecuritySummary />
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        title="Delete Project?"
        itemName={projectToDelete?.name || ''}
        itemType="project"
        cascadeInfo={deletePreview || undefined}
      />
    </div>
  );
}
